/* eslint-disable no-unused-vars */
import { useState, useId, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authAPI } from 'services/newPlatformAuth.service';
import { ClearSearchInput, Eye, EyeOff } from 'assets/icons';
import AppnextLogo from 'assets/images/Appnext_Logo.png';
import { Loader, CheckboxInner, Buttons } from 'components';
import { authActions } from 'store/auth/auth';
import { isPrefixInUrl } from 'utils';
import { routes } from 'config';
import { GridBackground } from './GridBackground';
import { Login2fa } from './Login2fa';
import * as S from './UserLogin.styled';

const captcha_sitekey = '5e592855-ea86-42d0-ab6b-b8daa7af0a19';

function isUsernameValid(username) {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(username);
}

function getRequires2fa(res) {
  return Boolean(res?.requires_2fa ?? res?.Requires_2fa ?? res?.requires2fa);
}

function getChallengeToken(res) {
  return res?.challenge_token ?? res?.Challenge_token ?? res?.challengeToken ?? '';
}

function getMaskedEmail(res) {
  return res?.masked_email ?? res?.Masked_email ?? res?.maskedEmail ?? '';
}

export const UserLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formId = useId();
  const btnId = useId();
  const captchaWidgetIdRef = useRef(null);
  const submitLoginRef = useRef(null);

  const [captchaLoader, setCaptchaLoader] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFaChallenge, setTwoFaChallenge] = useState(null);

  const [userLoginAction, result] = authAPI.useUserLoginMutation();

  const rememberMe = useSelector((state) => state.auth.rememberMe);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isCaptchaLoaded = useSelector((state) => state.auth.isCaptchaLoaded);
  const responseError = useSelector((state) => state.auth.responseError);

  const completeLogin = (res) => {
    dispatch(authActions.changeRememberMe(true));

    const {
      access_token,
      refresh_token,
      pb_email,
      pb_number,
      pb_roles,
      pb_username,
      isSamsungUser,
    } = res;
    dispatch(
      authActions.setAuthData({
        accessToken: access_token,
        refreshToken: refresh_token,
        pb_email,
        pb_username,
        isLoggedIn: true,
        isUserLogin: true,
        pb_number,
        pb_roles,
        isSamsungUser,
      }),
    );
    const link = routes.list.find((route) => route.id === 'campaign').url;
    navigate(link);
  };

  const handleBackFrom2fa = () => {
    setTwoFaChallenge(null);
    setCaptchaLoader(false);
    dispatch(authActions.setLoaded(true));
    dispatch(authActions.setResponseError(''));
    const widgetId = captchaWidgetIdRef.current;
    if (widgetId !== null && window.hcaptcha?.reset) {
      try {
        window.hcaptcha.reset(widgetId);
      } catch (e) {
        console.warn('hCaptcha reset failed', e);
      }
    }
  };

  // Keep latest login handler for hCaptcha callbacks (avoids stale closures).
  submitLoginRef.current = async (token) => {
    const usernameValue = document.querySelector('#username-input')?.value;
    const passwordValue = document.querySelector('#password-input')?.value;

    if (!usernameValue) {
      dispatch(authActions.setResponseError('Email is required'));
      setCaptchaLoader(false);
      dispatch(authActions.setLoaded(true));
      return;
    }

    if (!isUsernameValid(usernameValue)) {
      dispatch(authActions.setResponseError('Please enter a valid email address'));
      setCaptchaLoader(false);
      dispatch(authActions.setLoaded(true));
      return;
    }

    if (!passwordValue) {
      setPasswordError('Password is required');
      setCaptchaLoader(false);
      dispatch(authActions.setLoaded(true));
      return;
    }

    const requestData = {
      username: usernameValue,
      password: passwordValue,
      captchaToken: token,
    };

    try {
      const res = await userLoginAction(requestData).unwrap();
      if (!res) throw new Error('Login failed');

      if (getRequires2fa(res)) {
        setTwoFaChallenge({
          challengeToken: getChallengeToken(res),
          maskedEmail: getMaskedEmail(res),
        });
        dispatch(authActions.setResponseError(''));
        return;
      }

      if (!res.access_token) {
        throw new Error('Login failed');
      }

      completeLogin(res);
    } catch (err) {
      dispatch(authActions.setResponseError(err?.data?.Message || err?.message || 'Login failed'));
    } finally {
      setCaptchaLoader(false);
      dispatch(authActions.setLoaded(true));
    }
  };

  useEffect(() => {
    if (isPrefixInUrl()) {
      const prefix = `${window.location.host.split('.')[0]}.`;
      const href = window.location.href;
      window.location = href.replace(prefix, '');
      return undefined;
    }

    const captchaElement = document.getElementById('hcaptcha');
    if (
      !captchaElement ||
      !window.hcaptcha ||
      typeof window.hcaptcha.render !== 'function' ||
      captchaWidgetIdRef.current !== null
    ) {
      return undefined;
    }

    const widgetId = window.hcaptcha.render(captchaElement, {
      sitekey: captcha_sitekey,
      size: 'invisible',
      callback: (token) => {
        submitLoginRef.current?.(token);
      },
      // Same behavior as before: invisible captcha often needs a challenge retry.
      'error-callback': () => {
        console.error('Captcha error occurred');
        const id = captchaWidgetIdRef.current;
        if (id !== null && window.hcaptcha?.execute) {
          window.hcaptcha.execute(id);
        }
      },
      'expired-callback': () => {
        console.warn('Captcha expired');
        const id = captchaWidgetIdRef.current;
        if (id !== null && window.hcaptcha?.execute) {
          window.hcaptcha.execute(id);
        }
      },
    });
    captchaWidgetIdRef.current = widgetId;

    return () => {
      const id = captchaWidgetIdRef.current;
      if (id !== null && window.hcaptcha?.remove) {
        try {
          window.hcaptcha.remove(id);
        } catch (e) {
          console.warn('hCaptcha remove failed', e);
        }
      }
      captchaWidgetIdRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    dispatch(authActions.changeAuthType('user'));
  }, [dispatch]);

  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(authActions.clearAuthState());
    }
  }, [isLoggedIn, dispatch]);

  function handleUsernameChange(e) {
    const nextUsername = e.target.value;
    setUsername(nextUsername);

    if (!isButtonClicked) return;

    if (!nextUsername) {
      setUsernameError('Email is required');
      return;
    }
    if (!isUsernameValid(nextUsername)) {
      setUsernameError('Please enter a valid email address');
    } else if (usernameError) {
      setUsernameError('');
    }
  }

  function handlePasswordBlur(e) {
    if (!isButtonClicked) return;
    if (!e.target.value) setPasswordError('Password is required');
  }

  const handleChangeRememberMe = () => {
    dispatch(authActions.changeRememberMe(!rememberMe));
  };

  const triggerCaptcha = () => {
    setIsButtonClicked(true);
    setCaptchaLoader(true);
    dispatch(authActions.setLoaded(false));
    dispatch(authActions.setResponseError(''));

    const widgetId = captchaWidgetIdRef.current;
    if (widgetId !== null && window.hcaptcha && typeof window.hcaptcha.execute === 'function') {
      try {
        window.hcaptcha.execute(widgetId);
      } catch (error) {
        console.error('hCaptcha execution failed:', error);
        setCaptchaLoader(false);
        dispatch(authActions.setLoaded(true));
      }
    } else if (window.hcaptcha && typeof window.hcaptcha.execute === 'function') {
      // Fallback to default widget (same as historical behavior).
      try {
        window.hcaptcha.execute();
      } catch (error) {
        console.error('hCaptcha execution failed:', error);
        setCaptchaLoader(false);
        dispatch(authActions.setLoaded(true));
      }
    } else {
      console.warn('hCaptcha is not loaded; submitting without captcha token.');
      submitLoginRef.current?.(undefined);
      setCaptchaLoader(false);
    }
  };

  return (
    <S.Box>
      <GridBackground />
      {(result.isLoading || (captchaLoader && !isCaptchaLoaded)) && <Loader.Global delay={100} />}

      {twoFaChallenge ? (
        <Login2fa
          challengeToken={twoFaChallenge.challengeToken}
          maskedEmail={twoFaChallenge.maskedEmail}
          onBack={handleBackFrom2fa}
          onSuccess={completeLogin}
        />
      ) : (
        <S.StyledForm id={formId}>
          <div style={{ marginBottom: '24px' }}>
            <S.FlexCenter style={{ marginBottom: '16px' }}>
              <S.Label>Login</S.Label>
              <img src={AppnextLogo} alt='Appnext Logo' height={33} />
            </S.FlexCenter>
            <S.SeparatorGradient />
          </div>
          <S.InputGroup>
            <S.InputLabel htmlFor='username-input'>Email:</S.InputLabel>
            <S.InputWrapper error={Boolean(usernameError) || Boolean(responseError)}>
              <S.StyledInput
                placeholder='Enter your email'
                name='username'
                id='username-input'
                type='text'
                value={username}
                onChange={handleUsernameChange}
                onBlur={handleUsernameChange}
                onFocus={() => {
                  setUsernameError('');
                  dispatch(authActions.setResponseError(''));
                }}
              />
              {username && (
                <S.InputIcon onClick={() => setUsername('')}>
                  <ClearSearchInput />
                </S.InputIcon>
              )}
            </S.InputWrapper>
            {(responseError || usernameError) && (
              <S.ErrorText>{responseError || usernameError}</S.ErrorText>
            )}
          </S.InputGroup>

          <S.InputGroup>
            <S.InputLabel htmlFor='password-input'>Password:</S.InputLabel>
            <S.InputWrapper error={Boolean(passwordError) || Boolean(responseError)}>
              <S.StyledInput
                placeholder='Enter your password'
                name='password'
                id='password-input'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={handlePasswordBlur}
                onFocus={() => {
                  setPasswordError('');
                  dispatch(authActions.setResponseError(''));
                }}
              />
              <S.InputIcon onClick={() => setShowPassword((prev) => !prev)}>
                {showPassword ? <EyeOff /> : <Eye />}
              </S.InputIcon>
            </S.InputWrapper>
            {passwordError && <S.ErrorText>{passwordError}</S.ErrorText>}
          </S.InputGroup>
          <S.Block>
            <CheckboxInner
              label='Remember Me'
              name='remember_me'
              isChecked={Boolean(rememberMe)}
              id='remember_me'
              onChange={handleChangeRememberMe}
              style={{ gap: '8px' }}
              checkedStyle={{
                backgroundColor: rememberMe ? '#3599e7' : 'transparent',
                borderRadius: '5px',
              }}
              labelStyle={{
                color: '#a0aec0',
                fontSize: '14px',
                alignSelf: 'center',
                fontWeight: '400',
              }}
            />
            <S.Link to='/forgot-password'>Forgot Password?</S.Link>
          </S.Block>
          <S.BtnBox>
            <Buttons.PrimaryNormal
              style={{ width: '100%' }}
              id={btnId}
              onClick={triggerCaptcha}
              isDisabled={!username || !password || usernameError || passwordError}
            >
              Login
            </Buttons.PrimaryNormal>
          </S.BtnBox>
          <S.Footer>
            <S.Paragraph>New to Appnext?</S.Paragraph>
            <S.SignUpLink to='/signUp'>Sign Up Here</S.SignUpLink>
          </S.Footer>
        </S.StyledForm>
      )}

      {/* Always mounted so login ↔ 2FA does not destroy the widget mid-session */}
      <div id='hcaptcha' className='h-captcha' />
      <input type='hidden' name='recaptcha_token' id='appnextLoginToken' value='' />
    </S.Box>
  );
};
